import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
    Alert, Button, Drawer, Empty, Input, List, Modal, Space, Spin, Table, Tag, Typography, message,
} from 'antd'
import { HistoryOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons'
import {
    getCatalogDoc, getCatalogDocHistory, getCatalogDocs, restoreCatalogDoc, saveCatalogDoc,
} from './api'
import { docLineCount, docWordCount } from './model'

const { Text, Paragraph } = Typography
const { TextArea } = Input

const HISTORY_PAGE_SIZE = 20

// 2026-10-22 coder(lq): 文档页签 = 左 topic 列表 + 右 Markdown 文本框（不引入富文本依赖）+ 历史版本抽屉。
export default function DocPanel() {
    const [docs, setDocs] = useState([])
    const [docsLoading, setDocsLoading] = useState(false)
    const [docsError, setDocsError] = useState('')
    const [docsRefresh, setDocsRefresh] = useState(0)
    const [topic, setTopic] = useState(null)
    const [doc, setDoc] = useState(null)
    const [docLoading, setDocLoading] = useState(false)
    const [docError, setDocError] = useState('')
    const [draftTitle, setDraftTitle] = useState('')
    const [draftContent, setDraftContent] = useState('')
    const [saving, setSaving] = useState(false)
    const [historyOpen, setHistoryOpen] = useState(false)
    const [history, setHistory] = useState({ rows: [], total: 0, page: 1, size: HISTORY_PAGE_SIZE })
    const [historyLoading, setHistoryLoading] = useState(false)
    const [historyError, setHistoryError] = useState('')
    const docsSequence = useRef(0)
    const docSequence = useRef(0)

    useEffect(() => {
        const id = ++docsSequence.current
        setDocsLoading(true)
        setDocsError('')
        getCatalogDocs().then(result => {
            if (id !== docsSequence.current) return
            const rows = Array.isArray(result) ? result : []
            setDocs(rows)
            // 首次加载或当前 topic 被删除时，自动选中第一个文档，避免右侧空白。
            setTopic(previous => (previous && rows.some(row => row.topic === previous))
                ? previous
                : (rows[0] ? rows[0].topic : null))
        }).catch(error => {
            if (id !== docsSequence.current) return
            setDocs([])
            setDocsError(error && error.message ? error.message : '文档列表加载失败')
        }).finally(() => { if (id === docsSequence.current) setDocsLoading(false) })
        return () => { docsSequence.current += 1 }
    }, [docsRefresh])

    const loadDoc = useCallback(topicToLoad => {
        if (!topicToLoad) {
            setDoc(null)
            setDraftTitle('')
            setDraftContent('')
            return Promise.resolve()
        }
        const id = ++docSequence.current
        setDocLoading(true)
        setDocError('')
        return getCatalogDoc(topicToLoad).then(result => {
            if (id !== docSequence.current) return
            const value = result || {}
            setDoc(value)
            setDraftTitle(value.title || '')
            setDraftContent(value.content || '')
        }).catch(error => {
            if (id !== docSequence.current) return
            setDoc(null)
            setDocError(error && error.message ? error.message : '文档加载失败')
        }).finally(() => { if (id === docSequence.current) setDocLoading(false) })
    }, [])

    useEffect(() => {
        loadDoc(topic)
        return () => { docSequence.current += 1 }
    }, [topic, loadDoc])

    const dirty = !!doc && (draftTitle !== (doc.title || '') || draftContent !== (doc.content || ''))

    const selectTopic = nextTopic => {
        if (!nextTopic || nextTopic === topic) return
        if (!dirty) {
            setTopic(nextTopic)
            return
        }
        Modal.confirm({
            title: '有未保存的修改',
            content: '切换文档会丢失当前标题与 Markdown 正文的未保存修改。',
            okText: '放弃修改并切换',
            cancelText: '继续编辑',
            onOk: () => setTopic(nextTopic),
        })
    }

    const saveDoc = async () => {
        const title = draftTitle.trim()
        if (!topic) return
        if (!title) {
            message.error('请填写文档标题')
            return
        }
        setSaving(true)
        try {
            await saveCatalogDoc({ topic, title, content: draftContent })
            message.success('已保存')
            await loadDoc(topic)
            setDocsRefresh(value => value + 1)
        } catch (error) {
            // api.js 已统一 message.error，保留草稿供修正后重试。
        } finally {
            setSaving(false)
        }
    }

    const loadHistory = (page = 1, size = history.size) => {
        if (!topic) return
        setHistoryLoading(true)
        setHistoryError('')
        getCatalogDocHistory({ topic, page, size }).then(result => {
            const value = result || {}
            setHistory({ rows: value.rows || [], total: value.total || 0, page, size })
        }).catch(error => {
            setHistory({ rows: [], total: 0, page, size })
            setHistoryError(error && error.message ? error.message : '历史版本加载失败')
        }).finally(() => setHistoryLoading(false))
    }

    const openHistory = () => {
        if (!topic) return
        setHistoryOpen(true)
        loadHistory(1)
    }

    const restore = row => {
        Modal.confirm({
            title: `回滚到 v${row.version}？`,
            content: `将「${(doc && doc.title) || topic}」替换为 v${row.version}（${row.updateTime || '时间未知'}）的内容，服务端会生成一个新版本，历史版本不删除。当前编辑器里未保存的内容会丢失。`,
            okText: '确认回滚',
            cancelText: '取消',
            onOk: () => restoreCatalogDoc({ topic, version: row.version }).then(result => {
                const version = result && result.version
                message.success(version ? `已回滚，当前版本 v${version}` : '已回滚')
                setHistoryOpen(false)
                loadDoc(topic)
                setDocsRefresh(value => value + 1)
            }).catch(() => {
                // api.js 已统一提示，抽屉保持打开便于换版本重试。
            }),
        })
    }

    const historyColumns = [
        { title: 'topic', dataIndex: 'topic', width: 110, render: value => value || topic },
        { title: '版本', dataIndex: 'version', width: 90, render: value => <Tag color="blue">v{value}</Tag> },
        { title: '保存时间', dataIndex: 'updateTime', width: 170, render: value => value || '-' },
        { title: '修改人', dataIndex: 'updateBy', width: 130, render: value => value || '-' },
        {
            title: '操作', width: 110, render: (_, row) => <Button type="link" size="small"
                disabled={!!doc && Number(row.version) === Number(doc.version)} onClick={() => restore(row)}>回滚</Button>,
        },
    ]

    const currentVersion = doc && doc.version ? `v${doc.version}` : ''

    return <section className="cli-catalog-doc-layout">
        <aside className="cli-catalog-doc-topics">
            <div className="cli-catalog-doc-topics-heading">
                <Text strong>文档 topic</Text>
                <Button type="text" size="small" icon={<ReloadOutlined />} loading={docsLoading}
                    onClick={() => setDocsRefresh(value => value + 1)}>刷新</Button>
            </div>
            {docsError
                ? <Alert type="error" showIcon message={docsError}
                    action={<Button size="small" onClick={() => setDocsRefresh(value => value + 1)}>重试</Button>} />
                : <Spin spinning={docsLoading}>
                    <List dataSource={docs} className="cli-catalog-topic-list"
                        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无文档" /> }}
                        renderItem={item => <List.Item
                            className={item.topic === topic ? 'cli-catalog-topic cli-catalog-topic-active' : 'cli-catalog-topic'}
                            onClick={() => selectTopic(item.topic)}>
                            <div className="cli-catalog-topic-body">
                                <div className="cli-catalog-topic-title">{item.title || item.topic}</div>
                                <Text type="secondary" className="cli-catalog-topic-meta">
                                    <code>{item.topic}</code> · v{item.version || '-'}
                                    {Number(item.enabled) === 0 ? ' · 已停用' : ''}
                                </Text>
                                <Text type="secondary" className="cli-catalog-topic-meta">
                                    {item.updateTime || '-'}{item.updateBy ? ` · ${item.updateBy}` : ''}
                                </Text>
                            </div>
                        </List.Item>} />
                </Spin>}
        </aside>
        <div className="cli-catalog-doc-editor">
            {!topic && !docsLoading && !docsError && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="左侧还没有文档 topic，请先由后端初始化文档数据" />}
            {topic && <>
                <div className="cli-catalog-doc-meta">
                    <Space wrap size={8}>
                        <Text strong>{currentVersion || '未加载版本'}</Text>
                        {doc && doc.updateTime ? <Text type="secondary">最后更新 {doc.updateTime}{doc.updateBy ? ` · ${doc.updateBy}` : ''}</Text> : null}
                        {dirty ? <Tag color="orange">有未保存修改</Tag> : <Tag>未修改</Tag>}
                    </Space>
                    <Space>
                        <Button icon={<HistoryOutlined />} disabled={!doc || docLoading} onClick={openHistory}>历史版本</Button>
                    </Space>
                </div>
                {docError
                    ? <Alert type="error" showIcon message={docError}
                        action={<Button size="small" onClick={() => loadDoc(topic)}>重试</Button>} />
                    : <Spin spinning={docLoading}>
                        <Input value={draftTitle} size="large" maxLength={128} placeholder="文档标题"
                            className="cli-catalog-doc-title"
                            onChange={event => setDraftTitle(event.target.value)} />
                        <TextArea value={draftContent} rows={20} className="cli-catalog-markdown" spellCheck={false}
                            placeholder="Markdown 正文，例如：# 总览&#10;&#10;## 我该用哪个工具"
                            onChange={event => setDraftContent(event.target.value)} />
                        <div className="cli-catalog-doc-footer">
                            <Text type="secondary">{docWordCount(draftContent)} 字符 · {docLineCount(draftContent)} 行 · 纯文本 Markdown，预览列 P1</Text>
                            <Space>
                                <Button onClick={() => loadDoc(topic)} disabled={docLoading || saving}>重新加载</Button>
                                <Button type="primary" icon={<SaveOutlined />} loading={saving}
                                    disabled={!doc || !dirty || docLoading} onClick={saveDoc}>保存</Button>
                            </Space>
                        </div>
                    </Spin>}
            </>}
        </div>
        <Drawer title={topic ? `历史版本 · ${topic}` : '历史版本'} width={760} open={historyOpen}
            onClose={() => setHistoryOpen(false)} destroyOnClose>
            <Paragraph type="secondary">每次保存都会写入一条历史版本；回滚会生成新版本，不删除已有历史。</Paragraph>
            {historyError && <Alert type="error" showIcon message={historyError}
                action={<Button size="small" onClick={() => loadHistory(history.page, history.size)}>重试</Button>} />}
            <Table rowKey={row => `${row.topic || topic}-${row.version}`} size="middle" columns={historyColumns}
                dataSource={history.rows} loading={historyLoading} scroll={{ x: 640 }}
                pagination={history.total > 0 ? {
                    current: history.page, pageSize: history.size, total: history.total,
                    onChange: (page, size) => loadHistory(page, size),
                    showTotal: total => `共 ${total} 个历史版本`,
                } : false}
                locale={{ emptyText: historyError ? '加载失败，请点击重试' : '还没有历史版本，首次保存后生成' }} />
        </Drawer>
    </section>
}
